import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';

import { StyledSlider, StyledSingleSlider } from './Slider.styles';

import { SliderProps, slideDirection, SlideProps } from './Slider.types';

import { getReducerInitState, reducer } from './Slider.reducer';

import { useElementOnScreen, useIsBelowWidth } from '../../../';
import { SlideList } from './SlideList';
import { Arrows } from './controls/Arrows';
import { Dots } from './controls/Dots';

const Slider = ({
  customCurrentIndex = 0,
  data,
  renderComponentMethod,
  isInfinite = true,
  itemByLine = 1,
  lineBySlide = 1,
  numberOfItemsVisibleForRotateTransition = 5,
  translationPercentageForRotateTransition = 66,
  spacingForSlide = '0px',
  transitionType = 'fade',
  transitionTime = 300,
  transitionEasing = 'ease-out',
  isAutoplayEnabled = false,
  autoplayDelay = 3000,
  autoplayPauseOnHover = true,
  percentageVisibleForAutoplay = 50,
  slideListWrapper = ({ children }) => children,
  slideListPaddingLeft = '0px',
  slideListPaddingRight = '0px',
  controlsWrapper = ({ children }) => children,
  isSwipeEnabled = true,
  deltaForSwiping,
  isArrowsEnabled = true,
  arrowsComponentClassname = 'slider-arrow-list',
  arrowsComponentCustomClassname = '',
  arrowComponentClassname = 'slider-arrow',
  arrowComponentCustomClassnames = '',
  prevArrowChildren,
  nextArrowChildren,
  isDotsEnabled = true,
  dotsComponentClassname = 'slider-dot-list',
  dotsComponentCustomClassnames = '',
  dotComponentClassname = 'slider-dot',
  dotComponentCustomClassname = '',
  renderDotFunction,
  dotSrOnlyTextFunction,
  descriptionSrOnlyText,
  onSlideChange = () => {},
  onAfterSlideChange = () => {},
  onAutoplaySlideChange = () => {},
  onAfterAutoplaySlideChange = () => {},
  onArrowClick = () => {},
  onAfterArrowClick = () => {},
  onDotClick = () => {},
  onAfterDotClick = () => {},
  onSwipe = () => {},
  onAfterSwipe = () => {},
  disableSliderIfNotEnoughItems = false,
  componentClassname = 'slider',
  customClassnames = '',
  customStyle = '',
}: SliderProps) => {
  const haveLowerDeltaForSwiping = useIsBelowWidth(768);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const [numberOfItemBySlide, setNumberOfItemBySlide] = useState(1);
  const [numberOfSlide, setNumberOfSlide] = useState(
    Math.ceil(data.length / numberOfItemBySlide),
  );
  const [sliderState, sliderDispatch] = useReducer(
    reducer,
    getReducerInitState(customCurrentIndex, numberOfSlide - 1),
  );
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeStart, setSwipeStart] = useState<number | null>(null);
  const [swipeEnd, setSwipeEnd] = useState<number | null>(null);
  const [isChangingSlide, setIsChangingSlide] = useState<boolean>(false);
  const [autoplayStatus, setAutoplayStatus] = useState(isAutoplayEnabled);
  const [autoplayInterval, setAutoplayInterval] = useState<
    ReturnType<typeof setInterval> | number
  >(0);
  const [groupedData, setGroupedData] = useState<SlideProps[][]>([]);

  let defaultDescriptionSrOnlyText = '';
  if (!descriptionSrOnlyText) {
    defaultDescriptionSrOnlyText = `Slider with ${numberOfItemBySlide} slides shown at a time.`;
    if (isArrowsEnabled) {
      defaultDescriptionSrOnlyText += `Use the Previous and Next buttons to navigate.`;
    }
    if (isDotsEnabled) {
      defaultDescriptionSrOnlyText += `Use the slide dot buttons to jump to slides.`;
    }
  }

  let localDeltaForSwiping = deltaForSwiping;
  if (!localDeltaForSwiping) {
    localDeltaForSwiping = haveLowerDeltaForSwiping ? 40 : 150;
  }

  /**
   * Change active slide when customCurrentIndex is updated
   */
  useEffect(() => {
    sliderDispatch({
      type: 'goto',
      payload: { newSlideIndex: customCurrentIndex, source: 'prop' },
    });
  }, [customCurrentIndex]);

  useEffect(() => {
    setNumberOfItemBySlide(itemByLine * lineBySlide);
  }, [itemByLine, lineBySlide]);

  useEffect(() => {
    setNumberOfSlide(Math.ceil(data.length / numberOfItemBySlide));
  }, [data, numberOfItemBySlide]);

  useEffect(() => {
    sliderDispatch({
      type: 'reset',
      payload: {
        slideIndex: customCurrentIndex,
        maxIndex: numberOfSlide - 1,
      },
    });
  }, [numberOfSlide]);
  /**
   * End merge settings management
   */

  /**
   * Slide Management
   */
  useEffect(() => {
    setIsChangingSlide(true);

    if (onSlideChange) {
      onSlideChange(sliderState.currentSlideIndex, sliderState.latestSource);
    }
    switch (sliderState.latestSource) {
      case 'arrow':
        if (onArrowClick && sliderState.direction) {
          onArrowClick(
            sliderState.currentSlideIndex,
            sliderState.direction as slideDirection,
          );
        }
        break;
      case 'dot':
        if (onDotClick) {
          onDotClick(sliderState.currentSlideIndex);
        }
        break;
      case 'swipe':
        if (onSwipe) {
          onSwipe(sliderState.currentSlideIndex);
        }
        break;
      case 'autoplay':
        if (onAutoplaySlideChange) {
          onAutoplaySlideChange(sliderState.currentSlideIndex);
        }
        break;
      default:
        break;
    }

    setTimeout(() => {
      setIsChangingSlide(false);

      if (onAfterSlideChange) {
        onAfterSlideChange(
          sliderState.currentSlideIndex,
          sliderState.latestSource,
        );
      }

      switch (sliderState.latestSource) {
        case 'arrow':
          if (onAfterArrowClick && sliderState.direction) {
            onAfterArrowClick(
              sliderState.currentSlideIndex,
              sliderState.direction as slideDirection,
            );
          }
          break;
        case 'dot':
          if (onAfterDotClick) {
            onAfterDotClick(sliderState.currentSlideIndex);
          }
          break;
        case 'swipe':
          if (onAfterSwipe) {
            onAfterSwipe(sliderState.currentSlideIndex);
          }
          break;
        case 'autoplay':
          if (onAfterAutoplaySlideChange) {
            onAfterAutoplaySlideChange(sliderState.currentSlideIndex);
          }
          break;
        default:
          break;
      }
    }, transitionTime);
  }, [sliderState.currentSlideIndex]);
  /**
   * End slide Management
   */

  /**
   * Autoplay management
   */
  const isVisible = useElementOnScreen(sliderRef, {
    root: null,
    rootMargin: '0px',
    threshold: percentageVisibleForAutoplay / 100,
  });

  useEffect(() => {
    setAutoplayStatus(isAutoplayEnabled && isVisible);
  }, [isVisible, isAutoplayEnabled]);

  function handleSliderPointerEnter() {
    setAutoplayStatus(false);
    autoplayCancel();
  }

  function handleSliderPointerLeaving() {
    setAutoplayStatus(isAutoplayEnabled && isVisible);
  }

  function autoplayInit() {
    const onAutoplayChangingSlide = () => {
      sliderDispatch({ type: 'next', payload: { source: 'autoplay' } });
    };

    const getAutoplayInterval = () => {
      return setInterval(() => {
        onAutoplayChangingSlide();
      }, autoplayDelay);
    };

    setAutoplayInterval(getAutoplayInterval());
  }

  /**
   * Force type for autoplayInterval in order to avoid IDE errors
   */
  function autoplayCancel() {
    if (autoplayInterval) {
      clearInterval(autoplayInterval as ReturnType<typeof setInterval>);
      setAutoplayInterval(0);
    }
  }

  useEffect(() => {
    if (autoplayStatus && isVisible) {
      autoplayInit();
    } else {
      autoplayCancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplayStatus, isVisible]);

  useEffect(() => {
    // clean when unmounting component
    return () => {
      autoplayCancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /**
   * End autoplay management
   */

  /**
   * Swipe management
   */
  function handleSwipeStart(userSwipeStartPosition: number) {
    setSwipeStart(userSwipeStartPosition);
    setIsSwiping(true);
  }

  function handleSwipeMove(userSwipePosition: number) {
    if (isSwiping) {
      setSwipeEnd(userSwipePosition);
    }
  }

  function handleSwipeEnd() {
    if (isSwiping && swipeStart && swipeEnd) {
      if (
        swipeStart - swipeEnd > localDeltaForSwiping &&
        (isInfinite ||
          (!isInfinite && sliderState.currentSlideIndex < sliderState.maxIndex))
      ) {
        sliderDispatch({ type: 'next', payload: { source: 'swipe' } });
      } else if (
        swipeStart - swipeEnd < -localDeltaForSwiping &&
        (isInfinite || (!isInfinite && sliderState.currentSlideIndex > 0))
      ) {
        sliderDispatch({ type: 'prev', payload: { source: 'swipe' } });
      }
    }
    setIsSwiping(false);
    setSwipeStart(null);
    setSwipeEnd(null);
  }

  function handlePointerDown(event: MouseEvent<HTMLDivElement>) {
    handleSwipeStart(event.clientX);
  }

  function handlePointerMove(event: MouseEvent<HTMLDivElement>) {
    event.preventDefault();
    handleSwipeMove(event.clientX);
  }

  function handlePointerUp() {
    handleSwipeEnd();
  }

  function handlePointerLeave() {
    handleSwipeEnd();
  }

  function handlePointerCancel() {
    handleSwipeEnd();
  }

  const eventListToBindWhenSliderCanSwipe = {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
  };
  /**
   * End Swipe management
   */
  /**
   * Group data for Multi item in view management
   */
  const getGroupedData = useCallback(() => {
    return data.reduce((slideArray: SlideProps[][], slideItem, index) => {
      if (index % numberOfItemBySlide === 0) {
        slideArray.push([slideItem]);
      } else {
        slideArray[slideArray.length - 1].push(slideItem);
      }
      return slideArray;
    }, []);
  }, [data, numberOfItemBySlide]);

  useEffect(() => {
    setGroupedData(getGroupedData());
  }, [getGroupedData]);
  /**
   * End Group data for Multi item in view management
   */

  if (!data.length) {
    return null;
  }

  if (disableSliderIfNotEnoughItems && data.length <= numberOfItemBySlide) {
    return (
      <StyledSingleSlider itemByLine={itemByLine} lineBySlide={lineBySlide}>
        <div className={`${componentClassname}__single-slide`}>
          {groupedData[0].map((item, idx) => {
            return <div key={idx}>{renderComponentMethod({ item })}</div>;
          })}
        </div>
      </StyledSingleSlider>
    );
  }

  return (
    <StyledSlider
      customStyle={customStyle}
      currentSlideIndex={sliderState.currentSlideIndex}
      transitionTime={transitionTime}
      transitionType={transitionType}
      transitionEasing={transitionEasing}
      translationPercentageForRotateTransition={
        translationPercentageForRotateTransition
      }
      spacingForSlide={spacingForSlide}
      slideListPaddingLeft={slideListPaddingLeft}
      slideListPaddingRight={slideListPaddingRight}
      isDotsEnabled={isDotsEnabled}
      dotsComponentClassname={dotsComponentClassname}
      isArrowsEnabled={isArrowsEnabled}
      arrowsComponentClassname={arrowsComponentClassname}
      itemByLine={itemByLine}
      lineBySlide={lineBySlide}
      className={`${componentClassname} ${customClassnames} ${componentClassname}--is-${transitionType}-slide`}
      aria-label="slider"
      onPointerEnter={() => {
        if (isAutoplayEnabled && autoplayPauseOnHover) {
          handleSliderPointerEnter();
        }
      }}
      onPointerLeave={() => {
        if (isAutoplayEnabled && autoplayPauseOnHover) {
          handleSliderPointerLeaving();
        }
        if (isSwipeEnabled) {
          handlePointerLeave();
        }
      }}
      {...(isSwipeEnabled && eventListToBindWhenSliderCanSwipe)}
    >
      <p className="sr-only">
        {descriptionSrOnlyText || defaultDescriptionSrOnlyText}
      </p>
      {slideListWrapper({
        children: (
          <div className={`${componentClassname}__slide-list`} ref={sliderRef}>
            <SlideList
              groupedData={groupedData}
              renderComponentMethod={renderComponentMethod}
              numberOfItemsVisibleForRotateTransition={
                numberOfItemsVisibleForRotateTransition
              }
              translationPercentageForRotateTransition={
                translationPercentageForRotateTransition
              }
              transitionType={transitionType}
              isChangingSlide={isChangingSlide}
              isInfinite={isInfinite}
              sliderState={sliderState}
              numberOfSlide={numberOfSlide}
              spacingForSlide={spacingForSlide}
            />
          </div>
        ),
      })}
      {(isArrowsEnabled || isDotsEnabled) &&
        controlsWrapper({
          children: (
            <>
              {isArrowsEnabled && !!groupedData?.length && (
                <Arrows
                  componentClassname={arrowsComponentClassname}
                  customClassnames={arrowsComponentCustomClassname}
                  arrowComponentClassname={arrowComponentClassname}
                  arrowComponentCustomClassnames={
                    arrowComponentCustomClassnames
                  }
                  prevArrowChildren={prevArrowChildren}
                  nextArrowChildren={nextArrowChildren}
                  isInfinite={isInfinite}
                  sliderState={sliderState}
                  sliderDispatch={sliderDispatch}
                />
              )}

              {isDotsEnabled && (
                <Dots
                  numberOfDots={numberOfSlide}
                  currentSlideIndex={sliderState.currentSlideIndex}
                  sliderDispatch={sliderDispatch}
                  componentClassname={dotsComponentClassname}
                  customClassnames={dotsComponentCustomClassnames}
                  dotComponentClassname={dotComponentClassname}
                  dotComponentCustomClassname={dotComponentCustomClassname}
                  renderDotFunction={renderDotFunction}
                  dotSrOnlyTextFunction={dotSrOnlyTextFunction}
                />
              )}
            </>
          ),
        })}
    </StyledSlider>
  );
};
Slider.displayName = 'Slider';

export default Slider;
